import { useState } from "react";
import { Icon } from "./Icons";
import { money, useRateCard } from "../lib/store";
import {
  computeEstimate, defaultConfig, PHASE_OUTLINE, PHASE_DETAIL,
  engagedDisciplines, scopedLines,
} from "../lib/rates";
import type { Project } from "../lib/types";

/* The fee, phase by phase, in the open.
 *
 * A client asked to approve a fee wants to know what each stage costs, when it
 * falls due, and what they get for it. That was only reachable by clicking one
 * stage open at a time, and the per-discipline split wasn't shown at all. Here
 * it is expanded: every phase, its share, its amount, the running total, and —
 * where the disciplines are known — how that phase's money divides between them.
 * When a fee has been agreed the phases are scaled to the agreed sum, so the
 * figures reconcile to the contract rather than to an estimate. */

export function FeeByPhase({ p }: { p: Project }) {
  const rc = useRateCard();
  const [open, setOpen] = useState<string | null>(null);

  const config = p.estimate ?? defaultConfig(p.budget, p.currency);
  const est = computeEstimate({ ...config, budget: p.budget, currency: p.currency }, rc);
  const ccy = p.billing?.currency ?? p.currency;

  /* An agreed fee is the truth; the estimate only supplies the shape. */
  const agreed = p.billing?.contractSum;
  const scale = agreed && est.total > 0 ? agreed / est.total : 1;
  const phases = est.phases.map((ph) => ({ ...ph, amount: ph.amount * scale, cumulative: ph.cumulative * scale }));
  const total = agreed ?? est.total;
  if (!phases.length || !total) return null;

  /* Each discipline's own fee, split across the phases by the same percentages. */
  const disciplines = p.billing?.disciplines?.length
    ? p.billing.disciplines.map((d) => ({ name: d.name, amount: d.amount }))
    : est.lines.filter((l) => l.amount > 0).map((l) => ({ name: l.label, amount: l.amount * scale }));
  const engaged = engagedDisciplines(p);

  return (
    <div className="card reveal" style={{ overflow: "hidden", marginTop: 22 }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <span className="row" style={{ gap: 8 }}>
          <Icon.money width={15} style={{ color: "var(--accent-ink)" }} />
          <span className="kicker">The fee, stage by stage</span>
        </span>
        <span className="dim" style={{ fontSize: 12 }}>{agreed ? "as agreed" : "estimated"} · {money(total, ccy)}</span>
      </div>

      <p className="muted" style={{ fontSize: 13, lineHeight: 1.65, padding: "12px 18px 0", maxWidth: 680 }}>
        What each stage costs and what you receive for it. Open a stage to see what happens during it, what you get at
        the end, and how that stage&rsquo;s fee divides between the disciplines.
      </p>

      <div style={{ overflowX: "auto", padding: "12px 6px 0" }}>
        <table className="table" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ minWidth: 190 }}>Stage</th>
              <th style={{ textAlign: "right", width: 90 }}>Share</th>
              <th style={{ textAlign: "right", width: 130 }}>Fee</th>
              <th style={{ textAlign: "right", width: 130 }}>Running total</th>
              <th style={{ width: 34 }} />
            </tr>
          </thead>
          <tbody>
            {phases.map((ph) => {
              const isOpen = open === ph.key;
              const detail = PHASE_DETAIL[ph.key];
              return [
                <tr key={ph.key} onClick={() => setOpen(isOpen ? null : ph.key)} style={{ cursor: "pointer" }}>
                  <td>
                    <span style={{ fontWeight: 500 }}>{ph.label}</span>
                    {ph.construction && <span className="badge" style={{ fontSize: 8, marginLeft: 7 }}>during construction</span>}
                  </td>
                  <td style={{ textAlign: "right" }} className="mono">{ph.pctLabel}%</td>
                  <td style={{ textAlign: "right" }} className="mono">{money(ph.amount, ccy)}</td>
                  <td style={{ textAlign: "right" }} className="mono dim">{money(ph.cumulative, ccy)}</td>
                  <td style={{ textAlign: "right", color: "var(--ink-3)" }}>
                    <Icon.caretDown width={12} style={{ transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform .18s" }} />
                  </td>
                </tr>,
                isOpen ? (
                  <tr key={ph.key + "-detail"}>
                    <td colSpan={5} style={{ background: "var(--paper-2)" }}>
                      <div style={{ padding: "4px 4px 14px" }}>
                        {PHASE_OUTLINE[ph.key] && (
                          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.7, marginBottom: 14, maxWidth: 700 }}>{PHASE_OUTLINE[ph.key]}</p>
                        )}
                        <div className="row wrap" style={{ gap: 22, alignItems: "flex-start" }}>
                          {detail && (
                            <>
                              <div style={{ flex: 1, minWidth: 210 }}>
                                <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>What we do</div>
                                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, lineHeight: 1.65 }}>
                                  {scopedLines(detail.activities, engaged).map((x) => <li key={x}>{x}</li>)}
                                </ul>
                              </div>
                              <div style={{ flex: 1, minWidth: 210 }}>
                                <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>What you receive</div>
                                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, lineHeight: 1.65 }}>
                                  {scopedLines(detail.outputs, engaged).map((x) => <li key={x}>{x}</li>)}
                                </ul>
                              </div>
                            </>
                          )}
                          {disciplines.length > 0 && (
                            <div style={{ flex: 1, minWidth: 210 }}>
                              <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>This stage, by discipline</div>
                              <div className="col" style={{ gap: 3 }}>
                                {disciplines.map((d) => (
                                  <div key={d.name} className="row between" style={{ gap: 10, fontSize: 12.5 }}>
                                    <span className="dim" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                                    <span className="mono">{money(d.amount * (ph.pct ?? 0), ccy)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        {detail?.client && (
                          <div className="panel" style={{ padding: "9px 12px", marginTop: 14, maxWidth: 700 }}>
                            <div className="kicker" style={{ fontSize: 8.5 }}>Your part at this stage</div>
                            <div style={{ fontSize: 13, marginTop: 3, lineHeight: 1.55 }}>{detail.client}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : null,
              ];
            })}
            <tr>
              <td style={{ fontWeight: 600 }}>Total</td>
              <td style={{ textAlign: "right" }} className="mono">100%</td>
              <td style={{ textAlign: "right", fontWeight: 600 }} className="mono">{money(total, ccy)}</td>
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>

      <p className="dim" style={{ fontSize: 11.5, padding: "10px 18px 16px", lineHeight: 1.6 }}>
        {agreed
          ? "These are the agreed fee figures, divided across the stages of the appointment."
          : "An estimate at this stage — the figures firm up once the scope is agreed."}
        {" "}Fees exclude VAT and reimbursable expenses.
      </p>
    </div>
  );
}
